// frontend/src/pages/AdminDashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  UploadIcon,
  UserGroupIcon,
  XIcon,
  ExclamationIcon,
  EyeIcon,
  DownloadIcon,
  RefreshIcon,
  SearchIcon,
  PhoneIcon,
  DocumentReportIcon,
  CameraIcon,
  FilterIcon,
  MailIcon,
} from '@heroicons/react/outline';
import adminApi, {
  getDashboardStats,
  getOrganizations,
  importAssessmentExcel,
  exportCandidates,
  getAssessments,
  getCandidatesFiltered,
  getAssignedAssessments
} from '../services/adminApi';
import { showSuccess, showInfo, showError } from '../utils/toastUtils';
import ExportModal from '../components/ExportModal';
import PhotoModal from '../components/PhotoModal';

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 hover-card border border-gray-200">
    <div className="flex items-center">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="ml-4">
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <p className="text-2xl font-bold text-[#063b56]">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  // ─────────────────────────────────────────
  // Core state
  // ─────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [loading, setLoading] = useState(true);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userOrganizationId, setUserOrganizationId] = useState(null);
  const [userReady, setUserReady] = useState(false);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Reports table
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');       // ⭐ default = all
  const [selectedAssessmentFilter, setSelectedAssessmentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Assessments (dropdown)
  const [assignedAssessments, setAssignedAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Photo modal
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedCandidateForPhotos, setSelectedCandidateForPhotos] = useState(null);
  const [selectedAccessToken, setSelectedAccessToken] = useState(null);

  // Guard so the first fetch happens only after we've read the admin user
  const initialFetchDoneRef = useRef(false);

  // ─────────────────────────────────────────
  // Bootstrap: read role FIRST, then fetch
  // ─────────────────────────────────────────
  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const superAdmin = user.role === 'super_admin';
        setIsSuperAdmin(superAdmin);
        setUserRole(user.role);
        setUserOrganizationId(user.organization_id);
        setSelectedOrg(user.organization_id || '');
      } catch (e) {
        console.error('Error parsing adminUser:', e);
      }
    }
    setUserReady(true);
    fetchOrganizations();
    fetchAssessments();
    fetchAssignedAssessments();
  }, []);

  // ─────────────────────────────────────────
  // Debounce search input — does NOT refresh the page
  // ─────────────────────────────────────────
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // ─────────────────────────────────────────
  // Reports fetcher
  // ─────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      // Show all statuses when 'all' (backend treats missing status as all)
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (selectedAssessmentFilter) params.assessment_id = selectedAssessmentFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      // Use the general candidate listing endpoint so all statuses are visible
      const res = await getCandidatesFiltered(params);

      // Normalize the response shape — works whether backend returns
      // { candidates: [...], total } or { reports: [...], pagination }
      const rows = res.data.candidates || res.data.reports || [];
      const total =
        res.data.total ??
        res.data.pagination?.total ??
        rows.length;

      setReports(rows);
      setTotalReports(total);
      setTotalPages(
        res.data.totalPages ??
        res.data.pagination?.totalPages ??
        Math.max(1, Math.ceil(total / itemsPerPage))
      );
    } catch (error) {
      console.error('Error fetching reports:', error);
      showError('Failed to load candidates');
    } finally {
      setReportsLoading(false);
    }
  }, [currentPage, statusFilter, selectedAssessmentFilter, debouncedSearch]);

  // Trigger reports fetch whenever a dependency changes, but only after
  // we've read the admin user (avoids firing too early with a wrong org)
  useEffect(() => {
    if (!userReady) return;
    fetchReports();
  }, [userReady, fetchReports]);

  // Also refetch when the super admin changes the org selector
  useEffect(() => {
    if (userReady && isSuperAdmin) {
      setCurrentPage(1);
      fetchReports();
    }
    // eslint-disable-next-line
  }, [selectedOrg]);

  // ─────────────────────────────────────────
  // Fetchers (bootstrap)
  // ─────────────────────────────────────────
  const fetchOrganizations = async () => {
    try {
      const response = await getOrganizations();
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await getAssessments();
      setAssessments(response.data.assessments || []);
      if (response.data.assessments?.length > 0) {
        setSelectedAssessment(response.data.assessments[0].id);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };

  /**
   * ⭐ Assessment dropdown:
   *   - Super admin  → all assessments (getAssessments)
   *   - Org admin    → only assessments assigned to their org
   */
  const fetchAssignedAssessments = async () => {
    setAssessmentsLoading(true);
    try {
      const userData = localStorage.getItem('adminUser');
      const user = userData ? JSON.parse(userData) : null;
      const superAdmin = user?.role === 'super_admin';

      if (superAdmin) {
        // Super admin sees every assessment
        const res = await getAssessments();
        setAssignedAssessments(res.data.assessments || []);
      } else {
        // Org admin — use the scoped endpoint
        try {
          const res = await getAssignedAssessments();
          setAssignedAssessments(res.data.assessments || []);
        } catch (err) {
          // Fallback if the scoped endpoint isn't available yet
          console.warn('Scoped assessment endpoint failed, falling back:', err.message);
          const res = await getAssessments();
          setAssignedAssessments(res.data.assessments || []);
        }
      }
    } catch (error) {
      console.error('Error fetching assessments for dropdown:', error);
      setAssignedAssessments([]);
    } finally {
      setAssessmentsLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const orgId = isSuperAdmin ? selectedOrg : userOrganizationId;
      const response = await getDashboardStats(orgId);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      showError('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  // Initial stats load — after user is known
  useEffect(() => {
    if (userReady) fetchStats();
    // eslint-disable-next-line
  }, [userReady, selectedOrg]);

  // ─────────────────────────────────────────
  // Photo modal
  // ─────────────────────────────────────────
  const handleViewPhotos = async (row) => {
    try {
      let accessToken = row.access_token;
      const idToUse = row.candidate_id || row.id || row.session_id;

      if (!accessToken && idToUse) {
        const response = await getCandidateById(idToUse); 
        if (response.data.success) {
          accessToken = response.data.candidate?.access_token;
        }
      }

      if (!accessToken) {
        showError('No access token available for this candidate');
        return;
      }

      setSelectedCandidateForPhotos({
        id: idToUse,
        full_name: row.candidate_name || row.full_name,
        candidate_name: row.candidate_name || row.full_name,
      });
      setSelectedAccessToken(accessToken);
      setShowPhotoModal(true);
    } catch (error) {
      console.error('❌ Error fetching access token:', error);
      showError('Failed to load candidate data');
    }
  };

  // ─────────────────────────────────────────
  // Export
  // ─────────────────────────────────────────
  const handleExportCandidates = async (payload) => {
    setExporting(true);
    try {
      let fieldsArray = [];
      let factorsArray = [];

      if (Array.isArray(payload)) {
        fieldsArray = payload;
      } else if (payload && typeof payload === 'object') {
        fieldsArray = payload.fields || [];
        factorsArray = payload.factors || [];
      }

      const params = {
        fields: fieldsArray.join(','),
      };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (factorsArray.length > 0) {
        params.factors = factorsArray.map(f => f.factorId).join(',');
      }

      const response = await exportCandidates(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `candidates_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('✅ Candidates exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
      showError('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const openExportModal = () => setShowExportModal(true);

  // ─────────────────────────────────────────
  // Import modal
  // ─────────────────────────────────────────
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && /\.(xls|xlsx|xlsm)$/i.test(file.name)) {
      setImportFile(file);
      setImportResult(null);
    } else {
      alert('Please select a valid Excel file');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a file first');
      return;
    }
    setImporting(true);
    setImportProgress({ status: 'uploading', message: 'Uploading file...' });
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('assessment_id', 'MIPS-00001');
      const response = await importAssessmentExcel(formData);
      setImportResult(response.data);
      setImportProgress({ status: 'complete', message: 'Import completed!' });
      setTimeout(() => {
        fetchStats();
        fetchReports();
        fetchAssessments();
      }, 2000);
    } catch (error) {
      console.error(error);
      setImportResult({
        success: false,
        errors: [error.response?.data?.error || 'Import failed'],
      });
      setImportProgress({ status: 'error', message: 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setImportProgress(null);
  };

  // ─────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      started: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-blue-100 text-blue-800',
      timed_out: 'bg-red-100 text-red-800',
      invited: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircleIcon className="h-4 w-4 text-green-600" />,
      started: <ClockIcon className="h-4 w-4 text-yellow-600" />,
      pending: <UserGroupIcon className="h-4 w-4 text-blue-600" />,
      timed_out: <XIcon className="h-4 w-4 text-red-600" />,
      invited: <MailIcon className="h-4 w-4 text-purple-600" />,
    };
    return icons[status] || <UserGroupIcon className="h-4 w-4 text-gray-600" />;
  };

  const getGradeBadge = (grade) => {
    const g = (grade || '').toLowerCase();
    if (g.includes('high')) return 'bg-green-100 text-green-800';
    if (g.includes('average') || g.includes('medium')) return 'bg-yellow-100 text-yellow-800';
    if (g.includes('low')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const openReport = (row) => {
    const idToUse = row.session_id || row.candidate_id || row.id;
    if (!idToUse) {
      showError('No candidate ID available for this report');
      return;
    }
    window.open(`/admin/candidates/${idToUse}/report`, '_blank');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setSelectedAssessmentFilter('');
    setCurrentPage(1);
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'started', label: 'Started' },
    { value: 'completed', label: 'Completed' },
    { value: 'timed_out', label: 'Timed Out' },
  ];

  // ─────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#063b56]">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Welcome to the Assessment Platform
            {!isSuperAdmin && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                Organization Admin
              </span>
            )}
            {isSuperAdmin && (
              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                Super Admin
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {isSuperAdmin ? (
            <div className="flex items-center space-x-2">
              <FilterIcon className="h-5 w-5 text-gray-400" />
              <select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f] min-w-[180px]"
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
              <span className="font-medium">Org:</span>
              <span className="ml-2">
                {organizations.find(o => o.id === userOrganizationId)?.name || userOrganizationId || 'N/A'}
              </span>
            </div>
          )}

          {isSuperAdmin && (
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-[#ff9f2f] text-white px-4 py-2 rounded-lg hover:bg-[#e68a1a] flex items-center space-x-2 shadow-sm"
            >
              <UploadIcon className="h-5 w-5" />
              <span>Import Assessment</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Candidates"
          value={stats?.totalCandidates || 0}
          icon={UsersIcon}
          color="bg-[#063b56]"
        />
        <StatCard
          title="Completed"
          value={stats?.statusBreakdown?.find(s => s.status === 'completed')?.count || 0}
          icon={CheckCircleIcon}
          color="bg-green-600"
        />
        <StatCard
          title="Pending"
          value={stats?.statusBreakdown?.find(s => s.status === 'pending')?.count || 0}
          icon={UserGroupIcon}
          color="bg-[#ff9f2f]"
        />
        <StatCard
          title="Avg. Completion Time"
          value={stats?.averageCompletionTime ? `${Math.floor(stats.averageCompletionTime / 60)} min` : 'N/A'}
          icon={ClockIcon}
          color="bg-purple-600"
        />
      </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Card header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-[#063b56]">Candidates</h3>
              <p className="text-sm text-gray-500">
                View all candidate activity across statuses
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={openExportModal}
                disabled={exporting}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center space-x-1 disabled:opacity-50"
              >
                <DownloadIcon className="h-4 w-4" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters — search · status · assessment · refresh */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search — no form, no page reload */}
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  // Prevent any parent form submission / page reload
                  if (e.key === 'Enter') e.preventDefault();
                }}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
              />
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2 lg:w-52">
              <FilterIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Assessment filter — only assessments assigned to this admin */}
            <div className="flex items-center gap-2 lg:w-72">
              <FilterIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <select
                value={selectedAssessmentFilter}
                onChange={(e) => {
                  setSelectedAssessmentFilter(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={assessmentsLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f] disabled:opacity-50"
              >
                <option value="">
                  {assessmentsLoading ? 'Loading assessments…' : 'All Assessments'}
                </option>
                {assignedAssessments.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            {/* Refresh */}
            <button
              onClick={() => { handleClearFilters(); fetchReports(); }}
              disabled={reportsLoading}
              className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshIcon className={`h-4 w-4 ${reportsLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Active filters row */}
          {(searchTerm || statusFilter !== 'all' || selectedAssessmentFilter) && (
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <span>Active filters:</span>
              {searchTerm && (
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                  Search: "{searchTerm}"
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded capitalize">
                  Status: {statusFilter}
                </span>
              )}
              {selectedAssessmentFilter && (
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                  {assignedAssessments.find(a => a.id === selectedAssessmentFilter)?.name
                    || selectedAssessmentFilter}
                </span>
              )}
              <button
                onClick={handleClearFilters}
                className="text-[#ff9f2f] hover:underline font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assessment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reportsLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex justify-center items-center gap-2 text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ff9f2f]"></div>
                      Loading candidates…
                    </div>
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>No candidates found</p>
                    <p className="text-xs mt-1">
                      {selectedAssessmentFilter || statusFilter !== 'all' || searchTerm
                        ? 'Try adjusting or clearing your filters'
                        : 'Candidates will appear here once they start an assessment'}
                    </p>
                  </td>
                </tr>
              ) : (
                reports.map((row) => {
                  const name = row.candidate_name || row.full_name || '—';
                  const email = row.candidate_email || row.email || '—';
                  const phone = row.candidate_phone || row.phone || '—';
                  const assessment = row.assessment_name || row.assessment_id || '—';
                  const isCompleted = row.status === 'completed';

                  return (
                    <tr key={row.session_id || row.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-[#063b56] text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                            {(name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                            {row.department_name && (
                              <p className="text-xs text-gray-400 truncate">{row.department_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="truncate">{email}</div>
                        <div className="text-xs text-gray-400 flex items-center">
                          <PhoneIcon className="h-3 w-3 mr-1 text-gray-400" />
                          {phone}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {assessment}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(row.status)}`}>
                          {getStatusIcon(row.status)}
                          <span className="ml-1 capitalize">{row.status || 'pending'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isCompleted && row.grade ? (
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getGradeBadge(row.grade)}`}>
                            {row.grade}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {isCompleted ? (row.total_score ?? '—') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {isCompleted ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openReport(row)}
                              className="inline-flex items-center px-3 py-1.5 bg-[#063b56] text-white text-xs rounded-lg hover:bg-[#0a4a6a]"
                            >
                              <EyeIcon className="h-3.5 w-3.5 mr-1" />
                              View Report
                            </button>
                            <button
                              onClick={() => handleViewPhotos(row)}
                              className="text-[#063b56] hover:text-[#ff9f2f] transition-colors"
                              title="View Captured Photos"
                            >
                              <CameraIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalReports > itemsPerPage && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, totalReports)} of {totalReports} candidates
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || reportsLoading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || reportsLoading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Photo Modal */}
      <PhotoModal
        isOpen={showPhotoModal}
        onClose={() => {
          setShowPhotoModal(false);
          setSelectedCandidateForPhotos(null);
          setSelectedAccessToken(null);
        }}
        candidateId={selectedCandidateForPhotos?.id}
        accessToken={selectedAccessToken}
        candidateName={
          selectedCandidateForPhotos?.full_name ||
          selectedCandidateForPhotos?.candidate_name
        }
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportCandidates}
        exporting={exporting}
      />

      {/* Import Modal (Super Admin only) */}
      {showImportModal && isSuperAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[#063b56]">Import Assessment</h3>
              <button onClick={closeImportModal} className="text-gray-400 hover:text-gray-600">
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            {!importResult ? (
              <>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#ff9f2f] transition-colors relative">
                  <UploadIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Click to select or drag and drop</p>
                  <p className="text-sm text-gray-400">Excel files only (.xls, .xlsx, .xlsm)</p>
                  <input
                    type="file"
                    accept=".xls,.xlsx,.xlsm"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                {importFile && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-gray-700 truncate">{importFile.name}</span>
                    <span className="text-xs text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                )}
                <div className="mt-6 flex justify-end space-x-3">
                  <button onClick={closeImportModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || importing}
                    className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
                {importProgress && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{importProgress.message}</span>
                      {importProgress.status === 'uploading' && <span>Uploading...</span>}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${importProgress.status === 'complete'
                          ? 'bg-green-500'
                          : importProgress.status === 'error'
                            ? 'bg-red-500'
                            : 'bg-[#ff9f2f]'
                          }`}
                        style={{ width: importProgress.status === 'uploading' ? '50%' : '100%' }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div>
                {importResult.success ? (
                  <div className="text-center">
                    <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-green-600">Import Successful!</h4>
                    <p className="text-sm text-gray-600 mt-2">Assessment data has been imported successfully.</p>
                    <div className="mt-4 text-left bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Items:</strong> {importResult.itemsLoaded || 0}</p>
                      <p className="text-sm text-gray-700"><strong>Factors:</strong> {importResult.factorsLoaded || 0}</p>
                      <p className="text-sm text-gray-700"><strong>Domains:</strong> {importResult.domainsLoaded || 0}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <ExclamationIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-red-600">Import Failed</h4>
                    <div className="mt-4 text-left">
                      {importResult.errors?.map((error, idx) => (
                        <p key={idx} className="text-sm text-red-600 mb-1">• {error}</p>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeImportModal}
                    className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;